import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { style } from 'typestyle';
import PlusIcon from 'mdi-react/PlusIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import CalendarClockIcon from 'mdi-react/CalendarClockIcon';
import PencilOutlineIcon from 'mdi-react/PencilOutlineIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import AccountGroupOutlineIcon from 'mdi-react/AccountGroupOutlineIcon';
import ChevronLeftIcon from 'mdi-react/ChevronLeftIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import MedicalBagIcon from 'mdi-react/MedicalBagIcon';
import { useTranslation } from 'react-i18next';
import * as tableStyles from '../office-table.styles';
import * as accessStyles from '../office-access.styles';
import * as surfaceStyles from '../../../core/admin-surface.styles';
import PermittedUser from '../models/permitted-user';
import useUsers from '../hooks/users.hook';
import {
  createUser,
  deleteUser,
  getKnownUserName,
  setPermittedUserDetails,
  type PermittedUserDetails,
  type PermittedUserName,
} from '../services/users.services';
import { accessEndSortValue, accessEndStatus, formatOfficeDate } from '../utils/access-end';
import { detailsErrorMessage } from '../utils/details-error';
import { phoneSortValue, telHref } from '../utils/phone-number';
import {
  emptyAddRowName,
  forAddress,
  withLookup,
  withTypedFirstName,
  withTypedLastName,
} from '../utils/add-row-name';
import DeletePermittedUserModal from '../modals/delete-permitted-user-modal.component';
import EditPermittedUserModal from '../modals/edit-permitted-user-modal.component';
import ConfirmMedicChangeModal from '../modals/confirm-medic-change-modal.component';
import SchedulePermittedUserRemovalModal from '../modals/schedule-permitted-user-removal-modal.component';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import { Center } from '../../../core/components/center/center.component';
import notify from '../../../core/components/notify/notify.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import StyleConstants from '../../../core/styles.constants';
import stringIsNullOrWhitespace from '../../../core/utils/validation-utils';
import { isValidEmail } from '../../../core/utils/email-utils';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import { usePagedRows, useSortableRows } from '../../statistics/dashboard/components/table-controls.hook';
import SortableHeaderCell from '../../statistics/dashboard/components/sortable-header-cell.component';

interface Props {
  readonly officeId: string | undefined;
}

const ROWS_PER_PAGE_OPTIONS = [10, 15];

/** The row fields a details save changes, as the server stored them. */
const detailsPatch = (saved: PermittedUserDetails): Partial<PermittedUser> => ({
  sharedEmail: saved.sharedEmail ?? undefined,
  phoneNumber: saved.phoneNumber ?? undefined,
  isCompanyMedic: saved.isCompanyMedic,
});
const SEARCH_DEBOUNCE_MS = 200;

const OfficeEmailsGroup = ({ officeId }: Props): React.JSX.Element => {
  const keycloak = useKeycloak();
  const isMobile = useContext(IsMobileContext);
  const { t, i18n } = useTranslation();

  const [allUsers, setAllUsers] = useState<PermittedUser[] | undefined>(undefined);
  const [newEmail, setNewEmail] = useState<string>('');
  // Optional, so they are plain strings with no validation of their own. Their only job is to give
  // the access list something to show for somebody who has no profile of their own yet.
  // One value with named transitions rather than three loose pieces of state: the rule that matters
  // is the one connecting them, and `add-row-name.ts` is where it is stated and tested.
  const [name, setName] = useState(emptyAddRowName);
  const [addError, setAddError] = useState<string | undefined>(undefined);
  // The add field is revealed by the card head button (as in the mockup) instead of sitting in the
  // toolbar permanently.
  const [showAddRow, setShowAddRow] = useState<boolean>(false);
  // `searchInput` is what the field shows, `query` is what the table filters on — debounced so
  // long member lists aren't re-filtered and re-paged on every keystroke.
  const [searchInput, setSearchInput] = useState<string>('');
  const [query, setQuery] = useState<string>('');
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROWS_PER_PAGE_OPTIONS[0]);
  const [userToDelete, setUserToDelete] = useState<PermittedUser | undefined>(undefined);
  const [userToSchedule, setUserToSchedule] = useState<PermittedUser | undefined>(undefined);
  const [userToEdit, setUserToEdit] = useState<PermittedUser | undefined>(undefined);
  // The row whose medic flag is waiting on the confirmation dialog, and the one being saved — so
  // neither the button nor the dialog's accept can be pressed twice.
  const [userToToggleMedic, setUserToToggleMedic] = useState<PermittedUser | undefined>(undefined);
  const [togglingMedicId, setTogglingMedicId] = useState<string | undefined>(undefined);
  // Off by default: the list's job is still "everybody with access". The filter is how an
  // administrator asks the other question — "who is leaving" — without reading thirty rows.
  const [scheduledOnly, setScheduledOnly] = useState<boolean>(false);
  // The same idea for two more questions: "who are our medics" and "who can we phone".
  const [medicsOnly, setMedicsOnly] = useState<boolean>(false);
  const [withPhoneOnly, setWithPhoneOnly] = useState<boolean>(false);

  const { users, error } = useUsers({ id: officeId });

  useEffect(() => {
    const timeout = setTimeout(() => setQuery(searchInput), SEARCH_DEBOUNCE_MS);
    return (): void => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    if (users != null && allUsers == null) {
      setAllUsers([...users]);
    }
  }, [users, allUsers]);

  // Fills the name in for somebody this company already knows, so adding the same person to a
  // second office does not ask for their name again — and cannot record a second spelling of it.
  // Debounced because it runs while the address is being typed, and guarded by `current` so a slow
  // answer for a half-typed address cannot land on top of a newer one.
  useEffect(() => {
    const email = newEmail.trim();

    // Reconciled on every keystroke, before anything is fetched: a name the lookup filled in
    // belongs to the address it was fetched for and must not follow the administrator to a
    // different one.
    setName((current) => forAddress(current, email));

    if (!showAddRow || officeId == null || !isValidEmail(email)) {
      return undefined;
    }

    let live = true;

    const timeout = setTimeout(() => {
      getKnownUserName(officeId, email, keycloak)
        .then((known) => {
          // Guarded so a slow answer for a half-typed address cannot land on top of a newer one.
          if (!live) return;
          setName((current) => withLookup(current, email, known));
        })
        .catch(() => undefined);
    }, SEARCH_DEBOUNCE_MS);

    return (): void => {
      live = false;
      clearTimeout(timeout);
    };
  }, [showAddRow, officeId, newEmail, keycloak]);

  const scheduledCount = useMemo(
    () => (allUsers ?? []).filter((user) => accessEndStatus(user) === 'scheduled').length,
    [allUsers],
  );

  const medicCount = useMemo(() => (allUsers ?? []).filter((user) => user.isCompanyMedic).length, [allUsers]);

  const phoneCount = useMemo(() => (allUsers ?? []).filter((user) => !stringIsNullOrWhitespace(user.phoneNumber)).length, [allUsers]);

  const filteredUsers = useMemo(() => {
    const trimmedQuery = query.trim().toLowerCase();
    // Phone numbers are typed with all sorts of separators, so they are matched on digits alone —
    // "06 1234" finds "06-12345678".
    const queryDigits = trimmedQuery.replace(/\D/g, '');

    const matchesQuery = (user: PermittedUser): boolean =>
      !trimmedQuery ||
      user.email.toLowerCase().includes(trimmedQuery) ||
      (user.firstname ?? '').toLowerCase().includes(trimmedQuery) ||
      (user.lastname ?? '').toLowerCase().includes(trimmedQuery) ||
      (user.sharedEmail ?? '').toLowerCase().includes(trimmedQuery) ||
      (queryDigits.length > 0 && (user.phoneNumber ?? '').replace(/\D/g, '').includes(queryDigits));

    return (allUsers ?? []).filter(
      (user) =>
        matchesQuery(user) &&
        (!scheduledOnly || accessEndStatus(user) === 'scheduled') &&
        (!medicsOnly || user.isCompanyMedic) &&
        (!withPhoneOnly || !stringIsNullOrWhitespace(user.phoneNumber)),
    );
  }, [allUsers, query, scheduledOnly, medicsOnly, withPhoneOnly]);

  const { sortedRows, sortKey, sortDirection, toggleSort } = useSortableRows<PermittedUser>(
    filteredUsers,
    (user, key) => {
      switch (key) {
        case 'firstName':
          return user.firstname ?? '';
        case 'lastName':
          return user.lastname ?? '';
        case 'accessEnd':
          return accessEndSortValue(user);
        case 'phone':
          return phoneSortValue(user.phoneNumber);
        case 'email':
        default:
          return user.email;
      }
    },
    'email',
  );

  const { pagedRows, page, setPage, pageCount } = usePagedRows(sortedRows, rowsPerPage);

  // Narrowing the result set should always land the user on the first page of what they searched
  // for — usePagedRows only clamps to the last valid page, which can still be page 3 of 4.
  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, rowsPerPage, scheduledOnly, medicsOnly, withPhoneOnly]);

  // Returns the reason the address can't be added, or undefined when it is fine. Shown inline next
  // to the field instead of only as a toast, since the field stays on screen.
  const addEmailProblem = useCallback(
    (email: string): string | undefined => {
      if (stringIsNullOrWhitespace(email)) {
        return t('email-empty-message') ?? '';
      }

      if (!isValidEmail(email)) {
        return t('invalid-email-message') ?? '';
      }

      if ((allUsers ?? []).some((user) => user.email.toLowerCase() === email.trim().toLowerCase())) {
        return t('email-already-added-message') ?? '';
      }

      return undefined;
    },
    [allUsers, t],
  );

  const handleAddUser = useCallback(async (): Promise<void> => {
    if (!officeId) return;
    if (allUsers == null) return;

    const problem = addEmailProblem(newEmail);
    if (problem != null) {
      setAddError(problem);
      notify(problem, false);
      return;
    }

    const email = newEmail.trim();
    const firstName = name.firstName.trim();
    const lastName = name.lastName.trim();
    const result = await createUser(officeId, email, keycloak, firstName, lastName);

    // The service layer already reports the specific failure (401/409/…) through
    // showStatusCodeError, so only the success path needs its own message here.
    if (result) {
      const addedUser = new PermittedUser();
      addedUser.id = result.id;
      addedUser.email = email;
      // The name the server actually stored, not the one that was typed. A name the person already
      // set on their own profile wins, so echoing the input back could leave a name on screen that
      // was never saved — and this list is only loaded once, so nothing would come along to
      // correct it.
      addedUser.firstname = result.firstName ?? '';
      addedUser.lastname = result.lastName ?? '';

      setAllUsers([...allUsers, addedUser]);
      setNewEmail('');
      setName(emptyAddRowName);
      setAddError(undefined);
      setShowAddRow(false);
      notify(t('permitted-user-added-success-message'), true);
    }
  }, [officeId, allUsers, newEmail, name, addEmailProblem, keycloak, t]);

  // `acknowledged` says the dialog showed this person's future bookings and the administrator
  // went ahead. The server refuses with 409 without it, so it is passed through rather than
  // assumed here.
  //
  // `allOffices` removes them from every other office this administrator may reach as well. Only
  // this office's row leaves the list either way — the others belong to lists this page is not
  // showing — so the confirmation says how many offices were actually done, and says so plainly
  // when some were out of reach and were left alone.
  const handleDeleteUser = useCallback(
    async (acknowledged: boolean, allOffices: boolean): Promise<void> => {
      if (!officeId) return;
      if (allUsers == null || userToDelete == null) return;

      const outcome = await deleteUser(officeId, userToDelete.email, keycloak, acknowledged, allOffices);

      if (outcome) {
        setAllUsers(allUsers.filter((user) => user.email !== userToDelete.email));

        if (outcome.officesNotPermitted > 0) {
          notify(
            t('permitted-user-deleted-partial-message', {
              removed: outcome.officesRemovedFrom,
              skipped: outcome.officesNotPermitted,
            }),
            true,
          );
        } else if (outcome.officesRemovedFrom > 1) {
          notify(t('permitted-user-deleted-all-offices-message', { offices: outcome.officesRemovedFrom }), true);
        } else {
          notify(t('permitted-user-deleted-success-message'), true);
        }
      }

      setUserToDelete(undefined);
    },
    [officeId, allUsers, userToDelete, keycloak, t],
  );

  // The list is loaded once, so the row has to be updated in place — otherwise the date an
  // administrator has just set is invisible until they navigate away and back, and they set it
  // again.
  const handleScheduled = useCallback(
    (accessEndsOn?: string): void => {
      if (allUsers == null || userToSchedule == null) return;

      setAllUsers(
        allUsers.map((user) => {
          if (user.id !== userToSchedule.id) {
            return user;
          }

          return Object.assign(new PermittedUser(), user, { accessEndsOn });
        }),
      );

      notify(accessEndsOn ? t('schedule-removal-scheduled-message') : t('schedule-removal-called-off-message'), true);
      setUserToSchedule(undefined);
    },
    [allUsers, userToSchedule, t],
  );

  // The list is loaded once, so the edited row is patched in place for the same reason a newly
  // scheduled date is: otherwise the correction an administrator just made is invisible until they
  // navigate away and back, and they make it again.
  //
  // What goes into the row is what the server stored, never what was typed — it trims, and treats
  // blank as cleared.
  const patchUser = useCallback((officeUserId: string, patch: Partial<PermittedUser>): void => {
    setAllUsers((current) =>
      current?.map((user) => (user.id === officeUserId ? Object.assign(new PermittedUser(), user, patch) : user)),
    );
  }, []);

  const handleEdited = useCallback(
    (savedName?: PermittedUserName, savedDetails?: PermittedUserDetails, keepOpen = false): void => {
      if (savedDetails) {
        patchUser(savedDetails.officeUserId, detailsPatch(savedDetails));
      }

      // The name failed after the details were stored: the row is corrected, and the dialog stays
      // open to say what did not save.
      if (keepOpen) return;

      if (savedName) {
        patchUser(savedName.officeUserId, {
          firstname: savedName.firstName ?? '',
          lastname: savedName.lastName ?? '',
        });

        // Three different things happened depending on whether they have a sign-in account, and
        // "saved" alone would leave an administrator believing the two systems agree when the log
        // says otherwise.
        if (savedName.keycloak === 'Failed') {
          notify(t('edit-name-saved-keycloak-failed'), true);
        } else if (savedName.keycloak === 'NoAccount') {
          notify(t('edit-name-saved-no-account'), true);
        } else {
          notify(t('edit-name-saved'), true);
        }

        setUserToEdit(undefined);
        return;
      }

      notify(t('edit-user-saved'), true);
      setUserToEdit(undefined);
    },
    [patchUser, t],
  );

  // One click from the listing. Sends the flag alone, so it cannot overwrite a phone number or
  // shared email somebody else changed since this list was loaded.
  const handleToggleMedic = useCallback(
    async (user: PermittedUser): Promise<void> => {
      if (user.id == null) return;

      setTogglingMedicId(user.id);
      const outcome = await setPermittedUserDetails(user.id, { isCompanyMedic: !user.isCompanyMedic }, keycloak);
      setTogglingMedicId(undefined);
      setUserToToggleMedic(undefined);

      if (outcome.saved === undefined) {
        notify(detailsErrorMessage(outcome, (key) => t(key)), false);
        return;
      }

      patchUser(outcome.saved.officeUserId, detailsPatch(outcome.saved));
      notify(outcome.saved.isCompanyMedic ? t('medic-set-message') : t('medic-unset-message'), true);
    },
    [keycloak, patchUser, t],
  );

  // A filter that survives its own subject is a dead end: call off the last scheduled removal with
  // the filter on and the list empties with no obvious way back.
  useEffect(() => {
    if (scheduledOnly && scheduledCount === 0) {
      setScheduledOnly(false);
    }
  }, [scheduledOnly, scheduledCount]);

  useEffect(() => {
    if (medicsOnly && medicCount === 0) {
      setMedicsOnly(false);
    }
  }, [medicsOnly, medicCount]);

  useEffect(() => {
    if (withPhoneOnly && phoneCount === 0) {
      setWithPhoneOnly(false);
    }
  }, [withPhoneOnly, phoneCount]);

  const rangeLabel = (): string => {
    const from = sortedRows.length === 0 ? 0 : page * rowsPerPage + 1;
    const to = Math.min(sortedRows.length, (page + 1) * rowsPerPage);
    return t('table-range').replace('[x]', from.toString()).replace('[y]', to.toString()).replace('[z]', sortedRows.length.toString());
  };

  const renderEmpty = (title: string, hint: string): React.JSX.Element => (
    <div className={style(surfaceStyles.emptyState)}>
      <AccountGroupOutlineIcon size={30} />
      <span className={style(surfaceStyles.emptyTitle)}>{title}</span>
      <span className={style(surfaceStyles.emptyHint)}>{hint}</span>
    </div>
  );

  const renderBody = (): React.JSX.Element => {
    if (error) {
      return (
        <div className={style(accessStyles.cardPadded)}>
          <span className={style(tableStyles.errorMessage)}>{t('permitted-users-get-error')}</span>
        </div>
      );
    }

    if (allUsers == null) {
      return (
        <div className={style(accessStyles.loaderPad)}>
          <Center>
            <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
          </Center>
        </div>
      );
    }

    if (allUsers.length === 0) {
      return renderEmpty(t('no-existing-emails') ?? '', t('no-existing-emails-hint') ?? '');
    }

    if (sortedRows.length === 0) {
      return renderEmpty(t('no-emails-found') ?? '', t('no-emails-found-hint') ?? '');
    }

    return (
      <>
        <div className={accessStyles.tableWrap}>
          <table className={style(tableStyles.table)}>
            <thead>
              <tr>
                <SortableHeaderCell
                  className={style(tableStyles.tableHeaderCell)}
                  label={t('first-name')}
                  sortKeyName="firstName"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={toggleSort}
                />
                <SortableHeaderCell
                  className={style(tableStyles.tableHeaderCell)}
                  label={t('last-name')}
                  sortKeyName="lastName"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={toggleSort}
                />
                <SortableHeaderCell
                  className={style(tableStyles.tableHeaderCell)}
                  label={t('email')}
                  sortKeyName="email"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={toggleSort}
                />
                <SortableHeaderCell
                  className={style(tableStyles.tableHeaderCell)}
                  label={t('phone-number')}
                  sortKeyName="phone"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={toggleSort}
                />
                <SortableHeaderCell
                  className={style(tableStyles.tableHeaderCell)}
                  label={t('access-ends')}
                  sortKeyName="accessEnd"
                  activeSortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={toggleSort}
                />
                <th className={style(tableStyles.tableHeaderCell)}>{t('table-action')}</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((user) => (
                <tr
                  key={`${user.email} ${user.id ?? ''}`}
                  className={accessEndStatus(user) === 'scheduled' ? style(accessStyles.scheduledRow) : undefined}
                >
                  <td className={style(user.firstname ? tableStyles.tableCell : tableStyles.mutedCell)}>
                    <span className={style(accessStyles.nameWithBadge)}>
                      {user.firstname || '—'}
                      {user.isCompanyMedic && (
                        <span className={style(accessStyles.medicBadge)} title={t('company-medic').toString()}>
                          <MedicalBagIcon size={15} aria-label={t('company-medic').toString()} />
                        </span>
                      )}
                    </span>
                  </td>
                  <td className={style(user.lastname ? tableStyles.tableCell : tableStyles.mutedCell)}>{user.lastname || '—'}</td>
                  <td className={style(tableStyles.tableCell)}>{user.email}</td>
                  <td className={style(user.phoneNumber ? tableStyles.tableCell : tableStyles.mutedCell)}>
                    {user.phoneNumber ? <a href={telHref(user.phoneNumber)}>{user.phoneNumber}</a> : '—'}
                  </td>
                  <td className={style(accessEndStatus(user) === 'scheduled' ? tableStyles.tableCell : tableStyles.mutedCell)}>
                    {accessEndStatus(user) === 'scheduled' ? (
                      <span className={style(accessStyles.accessEndBadge)}>
                        <CalendarClockIcon size={14} />
                        {t('access-ends-on', { date: formatOfficeDate(user.accessEndsOn ?? '', i18n.language) })}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className={style(tableStyles.actionCell)}>
                    <button
                      type="button"
                      className={style(tableStyles.rowActionButton)}
                      aria-label={`${t('edit-user')} ${user.email}`}
                      title={t('edit-user').toString()}
                      onClick={(): void => setUserToEdit(user)}
                    >
                      <PencilOutlineIcon size={17} />
                    </button>
                    <button
                      type="button"
                      className={style(tableStyles.rowActionButton, user.isCompanyMedic ? accessStyles.medicActionActive : {})}
                      aria-label={`${user.isCompanyMedic ? t('unset-medic') : t('set-medic')} ${user.email}`}
                      aria-pressed={user.isCompanyMedic}
                      title={(user.isCompanyMedic ? t('unset-medic') : t('set-medic')).toString()}
                      disabled={togglingMedicId === user.id}
                      onClick={(): void => setUserToToggleMedic(user)}
                    >
                      <MedicalBagIcon size={17} />
                    </button>
                    <button
                      type="button"
                      className={style(tableStyles.rowActionButton)}
                      aria-label={`${t('schedule-removal')} ${user.email}`}
                      title={t('schedule-removal').toString()}
                      onClick={(): void => setUserToSchedule(user)}
                    >
                      <CalendarClockIcon size={17} />
                    </button>
                    <button
                      type="button"
                      className={style(tableStyles.rowActionButton)}
                      aria-label={`${t('delete')} ${user.email}`}
                      onClick={(): void => setUserToDelete(user)}
                    >
                      <DeleteOutlineIcon size={17} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={style(accessStyles.foot)}>
          <div className={style(accessStyles.footGroup)}>
            <span>{t('rows-per-page')}</span>
            <select
              className={style(tableStyles.rowsPerPageSelect)}
              value={rowsPerPage}
              onChange={(event): void => setRowsPerPage(Number(event.target.value))}
            >
              {ROWS_PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <span>{rangeLabel()}</span>
          <div className={style(accessStyles.footGroup)}>
            <button
              type="button"
              className={style(surfaceStyles.pageButton(page === 0))}
              disabled={page === 0}
              aria-label={t('previous-page').toString()}
              onClick={(): void => setPage(page - 1)}
            >
              <ChevronLeftIcon size={20} />
            </button>
            <button
              type="button"
              className={style(surfaceStyles.pageButton(page >= pageCount - 1))}
              disabled={page >= pageCount - 1}
              aria-label={t('next-page').toString()}
              onClick={(): void => setPage(page + 1)}
            >
              <ChevronRightIcon size={20} />
            </button>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={style(surfaceStyles.card)}>
      <div className={style(surfaceStyles.cardHead(isMobile))}>
        <div>
          <h2 className={style(surfaceStyles.cardTitle)}>{t('email-addresses')}</h2>
          <p className={style(surfaceStyles.cardSub)}>{t('email-addresses-card-subtitle')}</p>
        </div>
        <button
          type="button"
          className={style(surfaceStyles.primaryButton)}
          onClick={(): void => {
            setAddError(undefined);
            setShowAddRow((current) => !current);
          }}
          disabled={allUsers == null}
        >
          <PlusIcon size={16} />
          {t('add-email')}
        </button>
      </div>

      {showAddRow && (
        <div className={style(accessStyles.addRow(isMobile))}>
          <input
            type="email"
            maxLength={100}
            className={style(surfaceStyles.textInput, accessStyles.addRowEmailField)}
            placeholder={t('add-email').toString()}
            aria-label={t('add-email').toString()}
            aria-invalid={addError != null}
            value={newEmail}
            onChange={(event): void => {
              setNewEmail(event.target.value);
              setAddError(undefined);
            }}
            onKeyDown={(event): void => {
              if (event.key === 'Enter') {
                handleAddUser();
              }
            }}
          />
          {/* Optional. The add button stays enabled on the email alone, and Enter still submits
              from any of the three fields, so the address-only flow is unchanged. */}
          <input
            type="text"
            maxLength={100}
            className={style(surfaceStyles.textInput, accessStyles.addRowNameField)}
            placeholder={t('first-name-optional').toString()}
            aria-label={t('first-name-optional').toString()}
            value={name.firstName}
            readOnly={name.isOnFile}
            title={name.isOnFile ? t('name-already-on-file').toString() : undefined}
            onChange={(event): void => setName((current) => withTypedFirstName(current, event.target.value))}
            onKeyDown={(event): void => {
              if (event.key === 'Enter') {
                handleAddUser();
              }
            }}
          />
          <input
            type="text"
            maxLength={100}
            className={style(surfaceStyles.textInput, accessStyles.addRowNameField)}
            placeholder={t('last-name-optional').toString()}
            aria-label={t('last-name-optional').toString()}
            value={name.lastName}
            readOnly={name.isOnFile}
            title={name.isOnFile ? t('name-already-on-file').toString() : undefined}
            onChange={(event): void => setName((current) => withTypedLastName(current, event.target.value))}
            onKeyDown={(event): void => {
              if (event.key === 'Enter') {
                handleAddUser();
              }
            }}
          />
          {name.isOnFile && (
            <span className={style(accessStyles.addRowHint)}>{t('name-already-on-file')}</span>
          )}
          <button
            type="button"
            className={style(surfaceStyles.primaryButton)}
            onClick={handleAddUser}
            disabled={stringIsNullOrWhitespace(newEmail)}
          >
            {t('add')}
          </button>
          <button
            type="button"
            className={style(surfaceStyles.ghostButton)}
            onClick={(): void => {
              setNewEmail('');
              setName(emptyAddRowName);
              setAddError(undefined);
              setShowAddRow(false);
            }}
          >
            {t('cancel')}
          </button>
          {addError != null && (
            <span className={`${style(surfaceStyles.fieldError)} ${style(accessStyles.addRowError)}`}>{addError}</span>
          )}
        </div>
      )}

      <div className={style(surfaceStyles.toolbar)}>
        <div className={style(surfaceStyles.searchWrap(isMobile))}>
          <span className={style(surfaceStyles.searchIcon)}>
            <MagnifyIcon size={16} />
          </span>
          <input
            type="text"
            maxLength={100}
            className={style(surfaceStyles.searchInput)}
            placeholder={t('find-permitted-user').toString()}
            aria-label={t('find-permitted-user').toString()}
            value={searchInput}
            onChange={(event): void => setSearchInput(event.target.value)}
          />
        </div>

        {scheduledCount > 0 && (
          <button
            type="button"
            className={style(accessStyles.scheduledFilterButton(scheduledOnly))}
            aria-pressed={scheduledOnly}
            onClick={(): void => setScheduledOnly((current) => !current)}
          >
            {t('scheduled-for-removal-filter', { people: scheduledCount })}
          </button>
        )}

        {medicCount > 0 && (
          <button
            type="button"
            className={style(accessStyles.scheduledFilterButton(medicsOnly))}
            aria-pressed={medicsOnly}
            onClick={(): void => setMedicsOnly((current) => !current)}
          >
            <span className={style(accessStyles.filterButtonContent)}>
              <MedicalBagIcon size={15} />
              {t('medics-filter', { people: medicCount })}
            </span>
          </button>
        )}

        {phoneCount > 0 && (
          <button
            type="button"
            className={style(accessStyles.scheduledFilterButton(withPhoneOnly))}
            aria-pressed={withPhoneOnly}
            onClick={(): void => setWithPhoneOnly((current) => !current)}
          >
            {t('with-phone-number-filter', { people: phoneCount })}
          </button>
        )}
      </div>

      {renderBody()}

      <EditPermittedUserModal
        showModal={userToEdit != null}
        officeUserId={userToEdit?.id}
        email={userToEdit?.email ?? ''}
        firstName={userToEdit?.firstname ?? ''}
        lastName={userToEdit?.lastname ?? ''}
        sharedEmail={userToEdit?.sharedEmail ?? ''}
        phoneNumber={userToEdit?.phoneNumber ?? ''}
        isCompanyMedic={userToEdit?.isCompanyMedic ?? false}
        handleSaved={handleEdited}
        handleClose={(): void => setUserToEdit(undefined)}
      />

      <ConfirmMedicChangeModal
        showModal={userToToggleMedic != null}
        makeMedic={!(userToToggleMedic?.isCompanyMedic ?? false)}
        person={
          [userToToggleMedic?.firstname, userToToggleMedic?.lastname].filter(Boolean).join(' ') || (userToToggleMedic?.email ?? '')
        }
        isLoading={userToToggleMedic != null && togglingMedicId === userToToggleMedic.id}
        handleAccept={(): void => {
          if (userToToggleMedic == null) return;
          handleToggleMedic(userToToggleMedic).catch(() => {
            setTogglingMedicId(undefined);
            setUserToToggleMedic(undefined);
            notify(t('edit-user-failed'), false);
          });
        }}
        handleClose={(): void => {
          // Not while the save is in flight: closing then would hide its outcome.
          if (togglingMedicId == null) setUserToToggleMedic(undefined);
        }}
      />

      <DeletePermittedUserModal
        showModal={userToDelete != null}
        officeUserId={userToDelete?.id}
        email={userToDelete?.email ?? ''}
        handleAccept={(acknowledged, allOffices): void => {
          handleDeleteUser(acknowledged, allOffices).catch(() => undefined);
        }}
        handleClose={(): void => setUserToDelete(undefined)}
      />

      <SchedulePermittedUserRemovalModal
        showModal={userToSchedule != null}
        officeUserId={userToSchedule?.id}
        email={userToSchedule?.email ?? ''}
        currentAccessEndsOn={userToSchedule?.accessEndsOn}
        handleScheduled={handleScheduled}
        handleClose={(): void => setUserToSchedule(undefined)}
      />
    </div>
  );
};

export default OfficeEmailsGroup;
